import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { cn, LevelIndicatorIcon } from '@restworld/utils-common';
import { Badge, IBadgeProps, Heading } from '@restworld/ui-ds-base';

export function EmployerInfoLevelCard({
  missingInfoCount,
  title,
  content,
  additionalContent,
}: EmployerInfoLevelCardProps) {
  const { variant, text } = useEmployerInfoLevel(missingInfoCount);

  return (
    <Wrapper>
      <div className="flex items-center gap-3">
        <LevelIndicatorIcon variant={variant} />
        <Heading size={5} weight="semibold" color="neutral">
          {title}
        </Heading>
        <Badge variant={variant} className="w-fit">
          {text}
        </Badge>
      </div>
      <div className="flex flex-col gap-4">
        <InfoText>{content}</InfoText>
        {additionalContent && <InfoText>{additionalContent}</InfoText>}
      </div>
    </Wrapper>
  );
}
export default EmployerInfoLevelCard;

interface EmployerInfoLevelCardProps {
  missingInfoCount: number;
  title: string;
  content: string;
  additionalContent?: string;
}

enum InfoLevel {
  DANGER,
  WARNING,
  SUCCESS,
}

function useEmployerInfoLevel(missingInfoCount: number) {
  const { t: translateCommon } = useTranslation('common');

  let variant: IBadgeProps['variant'];
  let text: string;
  let level: InfoLevel;

  if (missingInfoCount >= 5) level = InfoLevel.DANGER;
  else if (missingInfoCount > 1 && missingInfoCount < 5) level = InfoLevel.WARNING;
  else level = InfoLevel.SUCCESS;

  switch (level) {
    case InfoLevel.WARNING:
      variant = 'warning';
      text = translateCommon('missing');
      break;
    case InfoLevel.SUCCESS:
      variant = 'success';
      text = translateCommon('partial');
      break;
    default:
      variant = 'danger';
      text = translateCommon('insufficient');
  }

  return { variant, text };
}

const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('p-4 bg-neutral-10 rounded-2xl min-w-[320px] border border-neutral-40', className)} {...props} />
);
const InfoText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-neutral-700 text-base font-medium', className)} {...props} />
);
